// ============================================================================
// OBT F3 — Park önerisi karar diyaloğu (onay / ret)
// ============================================================================
// Onay: gerekçe opsiyonel; onay uyarısı "kampanya kapsamına eklenecek".
// Ret:  gerekçe ZORUNLU; onay uyarısı "reddedilecek".
// Çift işlem `submitting` ile engellenir; backend nihai otoritedir.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import {
  MAX_COMMENT_LEN,
  isRequiredCommentValid,
  isOptionalCommentValid,
} from './reviewValidation';

/**
 * @param {object} props
 * @param {'approve'|'reject'|null} props.mode
 * @param {{park_name?: string}|null} props.suggestion
 * @param {() => void} props.onClose
 * @param {(comment: string) => Promise<void>} props.onConfirm
 * @param {string|null} [props.error]
 */
export default function SuggestedParkReviewDialog({
  mode,
  suggestion,
  onClose,
  onConfirm,
  error = null,
}) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const open = mode === 'approve' || mode === 'reject';
  const isReject = mode === 'reject';

  useEffect(() => {
    if (open) {
      setComment('');
      setSubmitting(false);
    }
  }, [open, mode]);

  const valid = isReject
    ? isRequiredCommentValid(comment)
    : isOptionalCommentValid(comment);

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(comment.trim());
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isReject ? 'Park Önerisini Reddet' : 'Park Önerisini Onayla'}
          </DialogTitle>
          <DialogDescription>
            {isReject
              ? `"${suggestion?.park_name || 'Bu park'}" önerisi reddedilecektir.`
              : `"${suggestion?.park_name || 'Bu park'}" bilgi talebi kapsamına eklenecek ve öneren Formen tarafından cevaplanabilecektir.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="suggestion-comment">
            {isReject ? 'Ret gerekçesi *' : 'Not (opsiyonel)'}
          </Label>
          <Textarea
            id="suggestion-comment"
            value={comment}
            maxLength={MAX_COMMENT_LEN}
            rows={3}
            placeholder={
              isReject ? 'Ret gerekçesini yazın…' : 'İsteğe bağlı açıklama…'
            }
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Vazgeç
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!valid || submitting}
            className={isReject ? 'bg-red-600 hover:bg-red-700' : undefined}
          >
            {submitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {isReject ? 'Reddet' : 'Onayla'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
